import { createSlice } from '@reduxjs/toolkit';
import * as fifo from '../../helpers/taxPlanner/fifoSelection';
import { computeImpact } from '../../helpers/taxPlanner/impact';
import { initialPick } from '../../helpers/taxPlanner/initialPick';
import {
  addStrategic,
  pruneStrategic,
  readParams,
  removeStrategic,
  writeLoss,
  writeTarget,
} from '../../helpers/taxPlanner/plannerStorage';
import { computeTotals, positionKey } from '../../helpers/taxPlanner/totals';

const emptyState = () => ({
  snapshot: null,
  selection: {},
  committedTotals: null,
  dirty: false,
  lastPick: null,
  beforeStrategic: {},
  params: { loss: '0', target: '0', strategic: [] },
  persistFailed: { loss: false, target: false, strategic: null },
  inputError: { loss: null, target: null },
  notice: null,
  toast: null,
  continued: false,
});

const findPosition = (state, key) => state.snapshot.positions.find((p) => positionKey(p) === key);

const pick = (state) =>
  initialPick(state.snapshot, {
    loss: state.params.loss,
    target: state.params.target,
    strategicKeys: state.params.strategic,
  });

const commit = (state) => {
  state.committedTotals = computeTotals(state.snapshot, state.selection, state.params.loss);
  state.dirty = false;
};

// Applies a helper result ({ok, quantity}) to the selection; a manual selection of a strategic position unmarks it.
const applyQuantity = (state, key, result) => {
  if (!result.ok) return;
  const qty = result.quantity;
  const current = state.selection[key];
  if (Number(qty) > 0) {
    state.selection[key] = qty;
    if (state.params.strategic.includes(key)) {
      state.params.strategic = state.params.strategic.filter((k) => k !== key);
      state.notice = { type: 'STRATEGIC_UNMARKED', key };
      delete state.beforeStrategic[key];
      state.dirty = true;
    }
  } else {
    delete state.selection[key];
  }
  if (current !== state.selection[key]) state.dirty = true;
};

const withPosition = (fn) => (state, { payload }) => {
  const pos = findPosition(state, payload.key);
  if (pos) applyQuantity(state, payload.key, fn(pos, payload, state.selection[payload.key] ?? '0'));
};

const slice = createSlice({
  name: 'taxPlanner',
  initialState: emptyState(),
  reducers: {
    enter(state, { payload: { snapshot, params } }) {
      Object.assign(state, emptyState());
      state.snapshot = snapshot;
      state.params = { loss: params.loss, target: params.target, strategic: [...params.strategic] };
      const result = pick(state);
      state.lastPick = result;
      state.selection = result.selection;
      commit(state);
    },
    // Mount and unmount of the page: a visit never sees the snapshot, selection or «Продовжити» of the one before (PO-1).
    leave() {
      return emptyState();
    },
    proceed(state) {
      state.continued = true;
    },
    selectLot: withPosition((pos, { n }) => fifo.selectLot(pos, n)),
    unselectLot: withPosition((pos, { n }) => fifo.unselectLot(pos, n)),
    togglePosition: withPosition((pos, _p, current) => fifo.togglePosition(pos, current)),
    setQuantity: withPosition((pos, { quantity }) => fifo.selectQuantity(pos, quantity)),
    toggleStrategic(state, { payload: { key } }) {
      if (state.params.strategic.includes(key)) {
        state.params.strategic = state.params.strategic.filter((k) => k !== key);
        if (state.beforeStrategic[key] !== undefined) {
          state.selection[key] = state.beforeStrategic[key];
          delete state.beforeStrategic[key];
          state.dirty = true;
        }
      } else {
        state.params.strategic.push(key);
        if (state.selection[key] !== undefined) {
          state.beforeStrategic[key] = state.selection[key];
          delete state.selection[key];
          state.dirty = true;
        }
      }
    },
    setLoss(state, { payload }) {
      state.params.loss = payload;
      state.dirty = true;
    },
    setTarget(state, { payload }) {
      state.params.target = payload;
    },
    recalculate(state) {
      commit(state);
    },
    repick(state) {
      state.beforeStrategic = {};
      const result = pick(state);
      state.lastPick = result;
      state.selection = result.selection;
      commit(state);
    },
    // The neutral toast of SCR-02 (mock .toast); the text is already translated.
    showToast(state, { payload }) {
      state.toast = payload;
    },
    clearToast(state) {
      state.toast = null;
    },
    clearNotice(state) {
      state.notice = null;
    },
    setPersistFailed(state, { payload }) {
      Object.assign(state.persistFailed, payload);
    },
    setInputError(state, { payload }) {
      Object.assign(state.inputError, payload);
    },
  },
});

export const taxPlannerReducer = slice.reducer;
export const {
  enter,
  leave,
  proceed,
  selectLot,
  unselectLot,
  togglePosition,
  setQuantity,
  toggleStrategic,
  setLoss,
  setTarget,
  recalculate,
  repick,
  clearNotice,
  showToast,
  clearToast,
  setPersistFailed,
  setInputError,
} = slice.actions;

export const selectImpact = (rootState) => {
  const { snapshot, selection, params } = rootState.taxPlanner;
  return computeImpact(snapshot, selection, params.loss);
};

const markOf = (snapshot, key) => {
  const p = snapshot.positions.find((x) => positionKey(x) === key);
  return p && { investingAccountId: p.investingAccountId, ticker: p.ticker, currency: p.currency };
};

export const enterPlanner = ({ snapshot, sub }) => (dispatch) => {
  // Marks of positions gone from a participating account are dropped on entry (AC-33, D15).
  if (snapshot.state === 'READY') pruneStrategic(sub, snapshot);
  const params = readParams(sub, snapshot.taxYear);
  dispatch(enter({ snapshot, params: { ...params, strategic: [...params.strategic] } }));
};

const changeParam = (field, write, set) => ({ sub, value }) => (dispatch, getState) => {
  const res = write(sub, getState().taxPlanner.snapshot.taxYear, value);
  if (!res.ok) {
    dispatch(setInputError({ [field]: res.reason }));
    return res;
  }
  dispatch(setInputError({ [field]: null }));
  dispatch(set(value.trim()));
  dispatch(setPersistFailed({ [field]: !res.persisted }));
  return res;
};

export const changeLoss = changeParam('loss', (sub, year, v) => writeLoss(sub, year, v), setLoss);
export const changeTarget = changeParam('target', (sub, _y, v) => writeTarget(sub, v), setTarget);

export const changeStrategic = ({ sub, key }) => (dispatch, getState) => {
  const state = getState().taxPlanner;
  const mark = markOf(state.snapshot, key);
  if (!mark) return;
  const res = state.params.strategic.includes(key) ? removeStrategic(sub, mark) : addStrategic(sub, mark);
  dispatch(toggleStrategic({ key }));
  dispatch(setPersistFailed({ strategic: res.persisted ? null : key }));
};

// Selection actions that, when they unmark a strategic position, persist the unmark right away (AC-23/AC-33).
const withUnmarkPersist = (action) => ({ sub, ...payload }) => (dispatch, getState) => {
  const before = getState().taxPlanner.params.strategic;
  dispatch(action(payload));
  const { params, snapshot } = getState().taxPlanner;
  before
    .filter((k) => !params.strategic.includes(k))
    .forEach((key) => {
      const res = removeStrategic(sub, markOf(snapshot, key));
      if (!res.persisted) dispatch(setPersistFailed({ strategic: key }));
    });
};

export const unselectLotPersisted = withUnmarkPersist(unselectLot);
export const selectLotPersisted = withUnmarkPersist(selectLot);
export const togglePositionPersisted = withUnmarkPersist(togglePosition);
export const setQuantityPersisted = withUnmarkPersist(setQuantity);
